import {
  SidebarItem,
  useSidebar,
} from '@/contexts/SidebarContext/SidebarContext';
import {Box, Divider, Typography} from '@mui/material';
import {useLocation, useNavigate} from 'react-router-dom';
import {SubSidebarItem} from './SubSidebarItem';

type SubSidebarContentProps = {
  items: SidebarItem[];
};

const SectionTitle = ({title}: {title: string}) => (
  <Typography
    variant="subtitle2"
    color="text.secondary"
    sx={{px: 1, py: 0.5, textTransform: 'uppercase', letterSpacing: 0.5}}
  >
    {title}
  </Typography>
);

const isItemSelected = (
  itemUrl: string,
  pathname: string,
  search: string,
  allItems: SidebarItem[],
  activeItemOverride?: string | null,
): boolean => {
  if (activeItemOverride != null) {
    return itemUrl.split('?')[0] === activeItemOverride;
  }

  const [itemPath, itemQuery] = itemUrl.split('?');

  if (!pathname.startsWith(itemPath)) {
    return false;
  }

  for (const sibling of allItems) {
    if (sibling.type === 'divider' || sibling.url === itemUrl) continue;
    const [siblingPath] = sibling.url.split('?');
    if (
      pathname.startsWith(siblingPath) &&
      siblingPath.length > itemPath.length
    ) {
      return false;
    }
  }

  if (!itemQuery) {
    const hasSiblingWithQueryOnSamePath = allItems.some(sibling => {
      if (sibling.type === 'divider' || sibling.url === itemUrl) return false;
      const [sibPath, sibQuery] = sibling.url.split('?');
      return sibPath === itemPath && sibQuery;
    });
    return hasSiblingWithQueryOnSamePath ? search === '' : true;
  }

  const itemParams = new URLSearchParams(itemQuery);
  const locationParams = new URLSearchParams(search);

  for (const [key, value] of itemParams.entries()) {
    if (locationParams.get(key) !== value) {
      return false;
    }
  }

  return true;
};

const buildNavigationUrl = (
  itemUrl: string,
  currentPathname: string,
  allItems: SidebarItem[],
): string => {
  const [itemPath, itemQuery] = itemUrl.split('?');

  const pathMatch = currentPathname.match(new RegExp(`^${itemPath}/([^/]+)`));
  if (pathMatch) {
    const candidate = `${itemPath}/${pathMatch[1]}`;
    const siblingOwnsPath = allItems.some(sibling => {
      if (sibling.type === 'divider' || sibling.url === itemUrl) return false;
      const [siblingPath] = sibling.url.split('?');
      return (
        currentPathname.startsWith(siblingPath) &&
        siblingPath.length > itemPath.length
      );
    });
    if (siblingOwnsPath) {
      return itemUrl;
    }
    const remainder = currentPathname.slice(candidate.length);
    if (remainder && remainder.startsWith('/')) {
      return itemUrl;
    }
    return itemQuery ? `${candidate}?${itemQuery}` : candidate;
  }

  return itemUrl;
};

export const SubSidebarContent = ({items}: SubSidebarContentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {activeItemOverride} = useSidebar();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      {items.map((item, index) => {
        if (item.type === 'divider') {
          return (
            <Box key={`divider-${index}`} sx={{width: '100%'}}>
              <Divider sx={{width: '100%', my: 1}} />
              {item.sectionTitle && <SectionTitle title={item.sectionTitle} />}
            </Box>
          );
        }
        const selected =
          item.isActive ??
          isItemSelected(
            item.url,
            location.pathname,
            location.search,
            items,
            activeItemOverride,
          );
        return (
          <Box key={item.id ?? item.url} sx={{width: '100%'}}>
            {item.sectionTitle && <SectionTitle title={item.sectionTitle} />}
            <SubSidebarItem
              icon={item.icon}
              label={item.label}
              destination={item.url}
              selected={selected}
              handleClick={() =>
                navigate(
                  item.opensRoot
                    ? item.url
                    : buildNavigationUrl(item.url, location.pathname, items),
                )
              }
              BadgeComponent={item.BadgeComponent}
              releaseDate={item.releaseDate}
            >
              {item.CountComponent && <item.CountComponent />}
            </SubSidebarItem>
          </Box>
        );
      })}
    </Box>
  );
};
